// src/pages/OrdersPage.jsx
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ordersAPI, ridersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { StatusBadge, Modal, Spinner, LoadingOverlay, EmptyState, SearchInput, SelectFilter, QRDisplay, toast } from '../components/UI';
import { usePolling } from '../hooks/usePolling';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' }, { value: 'Pending', label: 'Pending' },
  { value: 'Assigned', label: 'Assigned' }, { value: 'Picked Up', label: 'Picked Up' },
  { value: 'In Transit', label: 'In Transit' }, { value: 'Delivered', label: 'Delivered' },
  { value: 'Failed', label: 'Failed' },
];

const PACKAGE_TYPES = ['General', 'Document', 'Electronics', 'Clothing', 'Food & Perishables', 'Fragile', 'Medicine', 'Furniture', 'Jewelry', 'Other'];

function printQR(order) {
  const w = window.open('', '_blank');
  w.document.write(`<!DOCTYPE html><html><head><title>QR - ${order.package_id}</title>
  <style>
    body { font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; padding: 20px; }
    .box { border: 2px solid #000; border-radius: 12px; padding: 24px; text-align: center; max-width: 320px; }
    h2 { margin: 0 0 4px; font-size: 18px; }
    .pkg { font-family: monospace; font-size: 14px; color: #555; margin-bottom: 16px; }
    .info { font-size: 12px; color: #333; margin-top: 12px; text-align: left; }
    .info div { padding: 4px 0; border-bottom: 1px solid #eee; }
    .label { color: #999; font-size: 11px; }
    img { width: 200px; height: 200px; }
  </style></head><body>
  <div class="box">
    <h2>Honcho Xpress</h2>
    <div class="pkg">${order.package_id}</div>
    <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(order.qr_code)}" />
    <div class="info">
      <div><span class="label">To:</span> ${order.receiver_name}</div>
      <div><span class="label">Address:</span> ${order.address}</div>
      <div><span class="label">Phone:</span> ${order.phone}</div>
    </div>
  </div>
  <script>setTimeout(()=>{window.print();},400);<\/script>
  </body></html>`);
  w.document.close();
}

// ─── Create Order Modal ────────────────────────────────────────────────────────
function CreateOrderModal({ open, onClose, onCreated }) {
  const empty = {
    delivery_type: 'Office',
    sender_name: '', sender_phone: '', pickup_address: '',
    receiver_name: '', phone: '', address: '',
    package_type: 'General', package_description: '',
    delivery_fee: '', payment_method: 'Cash',
  };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isPickup = form.delivery_type === 'Pickup';

  const refs = {
    sender_name: React.useRef(), sender_phone: React.useRef(), pickup_address: React.useRef(),
    receiver_name: React.useRef(), phone: React.useRef(), address: React.useRef(),
    package_type: React.useRef(), package_description: React.useRef(), delivery_fee: React.useRef(),
  };

  const advance = (nextRef) => (e) => {
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) { e.preventDefault(); nextRef?.current?.focus(); }
  };

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  React.useEffect(() => {
    if (open && !result) {
      setForm({ ...empty });
      setErrors({});
      setTimeout(() => {
        if (form.delivery_type === 'Pickup') refs.sender_name.current?.focus();
        else refs.receiver_name.current?.focus();
      }, 120);
    }
  }, [open, result]);

  const validate = () => {
    const e = {};
    if (isPickup) {
      if (!form.sender_name.trim()) e.sender_name = 'Required';
      if (!form.sender_phone.trim()) e.sender_phone = 'Required';
      if (!form.pickup_address.trim()) e.pickup_address = 'Required';
    }
    if (!form.receiver_name.trim()) e.receiver_name = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    if (!form.address.trim()) e.address = 'Required';
    if (form.delivery_fee && isNaN(parseFloat(form.delivery_fee))) e.delivery_fee = 'Must be a number';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        sender_name: isPickup ? form.sender_name : form.receiver_name,
        receiver_name: form.receiver_name,
        address: form.address,
        phone: form.phone,
        package_type: form.package_type,
        package_description: form.package_description,
        delivery_fee: parseFloat(form.delivery_fee) || 0,
        payment_method: form.payment_method,
        ...(isPickup && {
          sender_phone: form.sender_phone,
          pickup_address: form.pickup_address,
        }),
      };
      const data = await ordersAPI.create(payload);
      setResult(data);
      onCreated();
      toast.success('Order created!');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to create order.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setForm({ ...empty }); setErrors({}); setResult(null); onClose(); };

  const inp = (name, nextRefName, placeholder, type = 'text') => (
    <input
      ref={refs[name]}
      className={`form-input ${errors[name] ? 'error' : ''}`}
      type={type}
      placeholder={placeholder}
      value={form[name]}
      onChange={e => set(name, e.target.value)}
      onKeyDown={advance(refs[nextRefName])}
    />
  );

  return (
    <Modal open={open} onClose={handleClose} size="lg"
      title={result ? 'Order Created' : 'Create New Order'}
      footer={result
        ? <button className="btn btn-primary btn-md" onClick={handleClose}>Done</button>
        : <>
          <button className="btn btn-ghost btn-md" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary btn-md" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size={16} /> : 'Create Order'}
          </button>
        </>
      }>

      {result ? (
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 20 }}>
            Order <strong>#{result.order.id}</strong> · <strong>{result.order.package_id}</strong> created successfully.
          </p>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', marginBottom: 8, letterSpacing: 0.5 }}>PACKAGE QR CODE</p>
              <QRDisplay value={result.order.qr_code} size={140} />
              {form.delivery_type === 'Office' && (
                <button className="btn btn-secondary btn-sm" style={{ marginTop: 8, width: '100%' }}
                  onClick={() => printQR(result.order)}>Print QR Label</button>
              )}
            </div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', marginBottom: 8, letterSpacing: 0.5 }}>CUSTOMER VERIFICATION CODE</p>
              <div style={{ background: 'var(--primary)', borderRadius: 12, padding: '20px 32px' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 40, fontWeight: 700, color: 'var(--accent)', letterSpacing: 8 }}>
                  {result.verification_code}
                </div>
              </div>
              <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 6 }}>Send to customer via SMS or call</p>
            </div>
          </div>
          <div style={{ background: 'var(--gray-50)', borderRadius: 10, padding: '14px 18px', textAlign: 'left', fontSize: 13, marginBottom: 12 }}>
            {[
              ['Package ID', result.order.package_id],
              ['Type', form.delivery_type],
              ['Receiver', result.order.receiver_name],
              ['Fee', '₦' + parseFloat(result.order.delivery_fee).toLocaleString()],
              ['Payment', result.order.payment_method],
              ['Address', result.order.address],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--gray-100)' }}>
                <span style={{ color: 'var(--gray-400)', fontWeight: 600 }}>{k}</span>
                <span style={{ color: 'var(--gray-800)', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '10px 14px', background: 'var(--warning-bg)', borderRadius: 8, fontSize: 13, color: 'var(--warning)', fontWeight: 500 }}>
            Verification code shown once only — save it before closing.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          <div className="form-group">
            <label className="form-label">Delivery Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { value: 'Office', desc: 'Package brought to office — print QR label for rider' },
                { value: 'Pickup', desc: 'Rider goes to collect from sender address first' },
              ].map(opt => (
                <button key={opt.value} type="button" onClick={() => set('delivery_type', opt.value)} style={{
                  flex: 1, padding: '12px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                  border: `2px solid ${form.delivery_type === opt.value ? 'var(--accent)' : 'var(--gray-200)'}`,
                  background: form.delivery_type === opt.value ? 'var(--accent-glow)' : 'var(--white)',
                  transition: 'all 0.15s',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: form.delivery_type === opt.value ? 'var(--primary)' : 'var(--gray-600)' }}>{opt.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {isPickup && (
            <div style={{ background: 'var(--gray-50)', border: '1.5px solid var(--accent)', borderRadius: 10, padding: '16px 16px 4px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 12 }}>
                Sender Details (Pickup Location)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Sender Name <span style={{ color: 'var(--danger)' }}>*</span></label>
                  {inp('sender_name', 'sender_phone', 'Full name of sender')}
                  {errors.sender_name && <span className="form-error">{errors.sender_name}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Sender Phone <span style={{ color: 'var(--danger)' }}>*</span></label>
                  {inp('sender_phone', 'pickup_address', '+234 800 000 0000', 'tel')}
                  {errors.sender_phone && <span className="form-error">{errors.sender_phone}</span>}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Pickup Address <span style={{ color: 'var(--danger)' }}>*</span></label>
                {inp('pickup_address', 'receiver_name', 'Where rider should go to collect the package')}
                {errors.pickup_address && <span className="form-error">{errors.pickup_address}</span>}
              </div>
            </div>
          )}

          <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 10, padding: '16px 16px 4px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 12 }}>
              Receiver Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Receiver Name <span style={{ color: 'var(--danger)' }}>*</span></label>
                {inp('receiver_name', 'phone', 'Full name')}
                {errors.receiver_name && <span className="form-error">{errors.receiver_name}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Receiver Phone <span style={{ color: 'var(--danger)' }}>*</span></label>
                {inp('phone', 'address', '+234 800 000 0000', 'tel')}
                {errors.phone && <span className="form-error">{errors.phone}</span>}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Delivery Address <span style={{ color: 'var(--danger)' }}>*</span></label>
              {inp('address', 'package_description', 'Full delivery address')}
              {errors.address && <span className="form-error">{errors.address}</span>}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Package Category</label>
              <select ref={refs.package_type} className="form-select" value={form.package_type}
                onChange={e => set('package_type', e.target.value)}>
                {PACKAGE_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Package Description</label>
              {inp('package_description', 'delivery_fee', 'Brief contents')}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Delivery Fee (N)</label>
              <input ref={refs.delivery_fee} className={`form-input ${errors.delivery_fee ? 'error' : ''}`}
                type="number" min="0" step="50" placeholder="e.g. 1500"
                value={form.delivery_fee} onChange={e => set('delivery_fee', e.target.value)} />
              {errors.delivery_fee && <span className="form-error">{errors.delivery_fee}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Payment Method</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                {['Cash', 'Transfer'].map(m => (
                  <button key={m} type="button" onClick={() => set('payment_method', m)} style={{
                    flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                    border: `2px solid ${form.payment_method === m ? 'var(--accent)' : 'var(--gray-200)'}`,
                    background: form.payment_method === m ? 'var(--accent-glow)' : 'var(--white)',
                    color: form.payment_method === m ? 'var(--primary)' : 'var(--gray-500)', transition: 'all 0.15s',
                  }}>{m}</button>
                ))}
              </div>
            </div>
          </div>

        </div>
      )}
    </Modal>
  );
}

// ─── Assign Rider Modal ────────────────────────────────────────────────────────
function AssignRiderModal({ open, onClose, order, riders, onAssigned }) {
  const [sel, setSel] = useState('');
  const [loading, setLoading] = useState(false);
  const avail = riders.filter(r => r.status !== 'Offline');

  const handleAssign = async () => {
    if (!sel) { toast.error('Select a rider.'); return; }
    setLoading(true);
    try { await ordersAPI.assign(order.id, sel); toast.success('Rider assigned!'); onAssigned(); onClose(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Failed.'); }
    finally { setLoading(false); setSel(''); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Assign Rider"
      footer={<>
        <button className="btn btn-ghost btn-md" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-md" onClick={handleAssign} disabled={loading || !sel}>
          {loading ? <Spinner size={16} /> : 'Assign Rider'}
        </button>
      </>}>
      <p style={{ fontSize: 13.5, color: 'var(--gray-500)', marginBottom: 14 }}>
        Order <strong>#{order?.id}</strong> · {order?.package_id}
      </p>
      {avail.length === 0
        ? <p style={{ textAlign: 'center', padding: '20px 0', color: 'var(--gray-400)' }}>No riders available right now.</p>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {avail.map(r => (
            <label key={r.user_id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
              border: `2px solid ${sel == r.user_id ? 'var(--accent)' : 'var(--gray-200)'}`,
              background: sel == r.user_id ? 'var(--accent-glow)' : 'var(--white)', transition: 'all 0.15s'
            }}>
              <input type="radio" style={{ display: 'none' }} checked={sel == r.user_id} onChange={() => setSel(r.user_id)} />
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, color: 'var(--accent)' }}>
                {r.name?.charAt(0)}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{r.phone}</div>
              </div>
            </label>
          ))}
        </div>}
    </Modal>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [payFilter, setPayFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [o, r] = await Promise.all([ordersAPI.getAll(), ridersAPI.getAll()]);
      setOrders(o || []);
      setRiders(r || []);
    } catch { toast.error('Failed to load.'); }
    finally { setLoading(false); }
  }, []);

  usePolling(fetchData, 10000);

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    const matchQ = !q || [o.package_id, o.receiver_name, o.address, String(o.id), o.package_type, o.sender_name].some(f => f?.toLowerCase().includes(q));
    return matchQ
      && (!statusFilter || o.status === statusFilter)
      && (!payFilter || o.payment_method === payFilter)
      && (!typeFilter || o.delivery_type === typeFilter);
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const totalFee = filtered.reduce((s, o) => s + parseFloat(o.delivery_fee || 0), 0);

  if (loading) return <LoadingOverlay message="Loading orders..." />;

  return (
    <div className="animate-fade-in">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1 className="page-title">Orders</h1>
          <p className="page-sub">{orders.length} total · {filtered.length} shown · N{totalFee.toLocaleString()} total fee</p>
        </div>
        <button className="btn btn-primary btn-md" onClick={() => setShowCreate(true)}>+ New Order</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by package, receiver, type..." />
        <SelectFilter value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
        <SelectFilter value={payFilter} onChange={setPayFilter} options={[
          { value: '', label: 'All Payments' },
          { value: 'Cash', label: 'Cash' },
          { value: 'Transfer', label: 'Transfer' },
        ]} />
        {/* ✅ FIXED: filter values now match DB values 'Pickup' and 'Office' */}
        <SelectFilter value={typeFilter} onChange={setTypeFilter} options={[
          { value: '', label: 'All Types' },
          { value: 'Pickup', label: 'Pickup' },
          { value: 'Office', label: 'Office' },
        ]} />
        {(search || statusFilter || payFilter || typeFilter) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setStatusFilter(''); setPayFilter(''); setTypeFilter(''); }}>Clear Filters</button>
        )}
      </div>

      <div className="card">
        {filtered.length === 0
          ? <EmptyState icon="" title="No orders found" subtitle="Adjust filters or create a new order."
            action={<button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>Create Order</button>} />
          : <div className="table-wrap">
            <table>
              <thead><tr>
                <th>ID</th><th>Package ID</th><th>Type</th><th>Sender / Receiver</th>
                <th>Phone</th><th>Payment</th><th>Fee (N)</th><th>Status</th><th>Date</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(o => (
                  <tr key={o.id}>
                    <td className="td-mono">#{o.id}</td>
                    <td>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>{o.package_id}</div>
                      <div style={{ fontSize: 10, marginTop: 2 }}>
                        {/* ✅ FIXED: was checking 'doorstep', now correctly checks 'Pickup' */}
                        <span style={{
                          padding: '1px 6px', borderRadius: 99, fontWeight: 700,
                          background: o.delivery_type === 'Pickup' ? '#EFF6FF' : '#F0FDF4',
                          color: o.delivery_type === 'Pickup' ? '#1D4ED8' : '#166534'
                        }}>
                          {o.delivery_type === 'Pickup' ? 'Pickup' : 'Office'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span style={{ background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, color: 'var(--gray-600)' }}>
                        {o.package_type}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{o.sender_name}</div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{o.receiver_name}</div>
                    </td>
                    <td className="td-mono" style={{ fontSize: 12 }}>{o.phone}</td>
                    <td>
                      <span style={{
                        padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                        background: o.payment_status === 'Paid' ? '#F0FDF4' : o.payment_method === 'Cash' ? '#FEF9EC' : '#EFF6FF',
                        color: o.payment_status === 'Paid' ? '#166534' : o.payment_method === 'Cash' ? '#B45309' : '#1D4ED8'
                      }}>
                        {o.payment_status === 'Paid' ? 'Paid' : o.payment_method}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                      N{parseFloat(o.delivery_fee || 0).toLocaleString()}
                    </td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="td-mono" style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                      {moment(o.created_at).format('MMM D, h:mm A')}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/orders/${o.id}`)}>View</button>
                        {o.status === 'Pending' && (
                          <button className="btn btn-secondary btn-sm" onClick={() => setAssignTarget(o)}>Assign</button>
                        )}
                        {/* ✅ FIXED: was checking 'office' (lowercase), now correctly checks 'Office' */}
                        {o.delivery_type === 'Office' && o.qr_code && (
                          <button className="btn btn-ghost btn-sm" onClick={() => printQR(o)}>Print</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>

      <CreateOrderModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={fetchData} />
      <AssignRiderModal open={!!assignTarget} onClose={() => setAssignTarget(null)} order={assignTarget} riders={riders} onAssigned={fetchData} />
    </div>
  );
}